// The DS avatar colour ramp, and the deterministic per-person pick over it.
//
// Figma: Symphony DS → "🟢 Avatars" → the `Symphony Avatar` component (270:3663). Its third variant
// prop, `Color`, is bound to the Figma `Avatars` variable group; that group is exported into
// `design-tokens/*.tokens.json` and generated into the `--ds-avatar-*` custom properties. So this
// module names TOKENS and never colour literals — `styles/design-tokens.css` is the only place a
// value lives, and `test/design-tokens.test.ts` is the guard.
//
// The INDEX and the two-letter initials are ported from native (iOS `ReferralAvatarColor.of` and the
// `Avatar` initials): Σ UTF-16 code units mod the palette length, so a given person lands on the same
// slot on every surface. `test/avatar-color.test.ts` pins the ramp, the index and the initials.

/** One `Color` value of the DS avatar: a pair of `--ds-*` token NAMES, read as `var(<name>)`. */
export interface AvatarSlot {
  bg: string;
  fg: string;
}

// The eight exported slots, in the SAME order as iOS `AccountAvatar.palette`. The neutral
// slot carries the primary ink; every coloured slot carries the dark on-accent ink (iOS: adaptive
// `textDefault` / `onAccent`, both near-black in light mode).
export const AVATAR_PALETTE: readonly AvatarSlot[] = [
  { bg: '--ds-avatar-01', fg: '--ds-text-primary' },
  { bg: '--ds-avatar-02', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-03', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-04', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-05', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-06', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-07', fg: '--ds-text-on-accent' },
  { bg: '--ds-avatar-08', fg: '--ds-text-on-accent' },
];

/**
 * The slot a person lands on — Σ UTF-16 code units mod 8, matching Swift `name.utf16.reduce(+)` and
 * Kotlin `name.sumOf { it.code }`. Stable for a given seed, so the same person keeps one colour
 * across sessions, surfaces and re-renders.
 *
 * `seed` is whatever identity the call site actually has: a display name, an account email, or an
 * opaque referral id. It only has to be stable — it is never parsed.
 */
export function avatarSlotFor(seed: string): AvatarSlot {
  let sum = 0;
  for (let i = 0; i < seed.length; i++) sum += seed.charCodeAt(i);
  const n = AVATAR_PALETTE.length;
  return AVATAR_PALETTE[((sum % n) + n) % n]!;
}

/**
 * Two-letter initials: the first letter of each of the first two space-separated words, uppercased.
 * Mirrors the native `Avatar`'s own initials (iOS `first + second`, Android `.take(2)`), so a person's
 * glyph and their colour both come from the shared algorithm.
 *
 * Call sites whose identity is not a display name keep their own derivation — a connector account is an
 * EMAIL, where `a.rossi@host` has to become "AR" rather than the first two letters of the local part.
 */
export function avatarInitials(name: string): string {
  const parts = name.split(' ').filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}
